import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ScrollManager — one place that owns page-level scroll behavior.
 *
 * - PUSH/REPLACE (navigating to a new page): start at the top.
 * - POP (back/forward): leave scroll alone so list pages can restore
 * the visitor's previous position (useRestoreListPosition).
 * - Hash links (#section): let the browser anchor-scroll instead.
 *
 * Without this, SPA navigation keeps the previous page's scroll offset,
 * so a new page opens halfway down.
 */
export function ScrollManager() {
  const { pathname, search, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === 'POP') return;
    if (hash) {
      // Client-side hash links (e.g. /#how from /pricing): scroll once the
      // target section has rendered, retrying briefly for lazy pages.
      const id = decodeURIComponent(hash.slice(1));
      let tries = 0;
      let raf = 0;
      const seek = () => {
        const el = document.getElementById(id);
        if (el) { el.scrollIntoView({ block: 'start' }); return; }
        if (tries++ < 60) raf = requestAnimationFrame(seek);
      };
      seek();
      return () => cancelAnimationFrame(raf);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search, hash, navigationType]);

  return null;
}
